import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  useCreateIdentity,
  type Identity,
} from "@/api/hooks/campaignForm"

/**
 * Identity picker with inline create-identity form.
 * Parity: `adgroup.js:303-377`.
 *
 *   - Loads identities for the selected advertiser.
 *   - Auto-selects when exactly 1 is returned.
 *   - Syncs `unified_identity_type` + `unified_identity_authorized_bc_id`
 *     from the selected option's metadata.
 *   - Inline form to create a new identity via multipart POST; on
 *     success the new row is appended and auto-selected.
 *   - Create errors surface via the legacy `alert()` parity wording.
 */

const TYPE_LABELS: Record<string, string> = {
  TT_USER: "TikTok",
  CUSTOMIZED_USER: "Custom",
  AUTH_CODE: "Auth Code",
  BC_AUTH_TT: "BC Auth",
}

/** TikTok expects a square avatar; center-crop to min(w,h) and export JPEG. */
async function prepareSquareAvatarFile(
  file: File,
): Promise<{ file: File; note: string | null }> {
  if (!file.type.startsWith("image/")) {
    return { file, note: null }
  }

  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error("Could not read image"))
      el.src = url
    })

    const w = img.naturalWidth
    const h = img.naturalHeight
    if (!w || !h) {
      return { file, note: null }
    }
    if (w === h) {
      return { file, note: null }
    }

    const size = Math.min(w, h)
    const sx = Math.floor((w - size) / 2)
    const sy = Math.floor((h - size) / 2)

    const canvas = document.createElement("canvas")
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext("2d")
    if (!ctx) {
      return { file, note: null }
    }
    ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size)

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92),
    )
    if (!blob) {
      return { file, note: null }
    }

    const base = file.name.replace(/\.[^.]+$/, "") || "avatar"
    const out = new File([blob], `${base}-1x1.jpg`, { type: "image/jpeg" })
    return {
      file: out,
      note: `Cropped to ${size}×${size} (was ${w}×${h}) for TikTok 1:1.`,
    }
  } catch {
    return { file, note: null }
  } finally {
    URL.revokeObjectURL(url)
  }
}

interface Props {
  advertiserId: string
  identities: Identity[] | undefined
  canUseCustomIdentity?: boolean
  isLoading: boolean
  isError: boolean
  selectedId: string | null
  onSelect: (id: string, identity_type: string, bc_id: string) => void
}

export function IdentityPicker({
  advertiserId,
  identities,
  canUseCustomIdentity = true,
  isLoading,
  isError,
  selectedId,
  onSelect,
}: Props) {
  const [formOpen, setFormOpen] = useState(false)
  const [displayName, setDisplayName] = useState("")
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [avatarNote, setAvatarNote] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  const createMutation = useCreateIdentity()

  const list = identities ?? []

  const handleCreate = async () => {
    const name = displayName.trim()
    if (!name) {
      window.alert("Please enter a display name.")
      return
    }
    setCreateError(null)
    try {
      const identity = await createMutation.mutateAsync({
        advertiserId,
        displayName: name,
        imageFile,
      })
      onSelect(
        identity.id,
        identity.identity_type || "CUSTOMIZED_USER",
        identity.identity_authorized_bc_id || "",
      )
      setDisplayName("")
      setImageFile(null)
      setAvatarNote(null)
      setFormOpen(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      window.alert("Error creating identity: " + msg)
      setCreateError(msg)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          {/* Shadow input that participates in the browser's native form
              validation. Radix Select renders a <button> + popover, so the
              standard `required` attribute has no effect on the trigger
              itself. An absolutely-positioned, pointer-event-disabled text
              input mirrors the selected id and lets the browser block
              submit with its built-in "Please fill out this field" popup,
              pointed at the trigger area. */}
          <input
            tabIndex={-1}
            aria-hidden="true"
            required
            value={selectedId ?? ""}
            onChange={() => {}}
            className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
          />
          <Select
            value={selectedId ?? ""}
            disabled={isLoading || isError}
            onValueChange={(value) => {
              const match = list.find((i) => i.id === value)
              onSelect(
                value,
                match?.identity_type || "CUSTOMIZED_USER",
                match?.identity_authorized_bc_id || "",
              )
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={
                  isLoading
                    ? "Loading identities..."
                    : isError
                      ? "Failed to load identities"
                      : "Select TikTok page..."
                }
              />
            </SelectTrigger>
            <SelectContent>
              {list.map((i) => {
                const typeLabel = TYPE_LABELS[i.identity_type] ?? i.identity_type
                return (
                  <SelectItem key={i.id} value={i.id}>
                    {i.name} ({typeLabel})
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setFormOpen((v) => !v)}
        >
          + Create New
        </Button>
      </div>
      {!canUseCustomIdentity ? (
        <p className="mt-1 text-xs text-amber-300/90">
          Custom Identity may not work on this ad account
        </p>
      ) : null}
      {formOpen ? (
        <div className="mt-2 space-y-2 rounded-md border border-brand-border bg-brand-bg/40 p-3">
          <div>
            <Label className="text-xs">Display Name</Label>
            <Input
              type="text"
              placeholder="Enter display name"
              value={displayName}
              maxLength={100}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">
              Avatar (optional) — TikTok needs 1:1; non-square images are
              auto-cropped (center).
            </Label>
            <input
              type="file"
              accept="image/*"
              className="block w-full text-sm text-white"
              onChange={async (e) => {
                const raw = e.target.files?.[0] ?? null
                e.target.value = ""
                setCreateError(null)
                setAvatarNote(null)
                if (!raw) {
                  setImageFile(null)
                  return
                }
                if (!raw.type.startsWith("image/")) {
                  setImageFile(null)
                  setCreateError("Please choose an image file.")
                  return
                }
                try {
                  const { file, note } = await prepareSquareAvatarFile(raw)
                  setImageFile(file)
                  setAvatarNote(note)
                } catch {
                  setImageFile(raw)
                  setAvatarNote(null)
                }
              }}
            />
            {avatarNote ? (
              <p className="mt-1 text-xs text-white/70">{avatarNote}</p>
            ) : null}
          </div>
          {createError ? (
            <div className="text-xs text-red-400">{createError}</div>
          ) : null}
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Creating..." : "Create"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setFormOpen(false)
                setCreateError(null)
                setDisplayName("")
                setImageFile(null)
                setAvatarNote(null)
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
